import AuthRequiredLink from '../components/AuthRequiredLink';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePageInit } from '../hooks/usePageInit';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuthGuard } from '../hooks/useAuthGuard';
import './ContactPage.css';
import { useMemo, useState } from 'react';

export default function ContactPage() {
  usePageInit();
  usePageTitle('お問い合わせ');
  useAuthGuard();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const privacyPolicyLink = useMemo(
    () => (
      <AuthRequiredLink className="link" to="/privacy-policy">
        プライバシーポリシー
      </AuthRequiredLink>
    ),
    []
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);

    const form = e.currentTarget;
    const fd = new FormData(form);
    const name = String(fd.get('name') || '').trim();
    const email = String(fd.get('email') || '').trim();
    const companyName = String(fd.get('companyName') || '').trim();
    const message = String(fd.get('message') || '').trim();
    const privacyAccepted = fd.get('privacyAccepted') === 'on';

    if (!name || !email || !message) {
      setSubmitError('必須項目を入力してください。');
      return;
    }
    if (!privacyAccepted) {
      setSubmitError('プライバシーポリシーへの同意が必要です。');
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch('/api/inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          companyName: companyName || null,
          message,
          privacyAccepted,
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Request failed: ${res.status}`);
      }
      // APIがSPAフォールバック(index.html)等を返してしまう“偽成功”を防ぐ
      const json = (await res.json().catch(() => null)) as any;
      if (!json?.ok) {
        throw new Error('Invalid API response');
      }
      setSubmitted(true);
      form.reset();
      // Materializeの入力ラベル/textarea表示をリセット（値はform.reset()でクリア済み）
      window.setTimeout(() => {
        const M = (window as any).M;
        if (M?.updateTextFields) M.updateTextFields();
        const textarea = form.querySelector<HTMLTextAreaElement>('textarea.materialize-textarea');
        if (textarea) {
          // reset後に高さだけ残るケースがあるため明示的に戻す
          textarea.style.height = '';
          if (M?.textareaAutoResize) M.textareaAutoResize(textarea);
        }
      }, 0);
    } catch (err) {
      console.error('Failed to submit inquiry:', err);
      setSubmitError('送信に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="m-application theme--light transition-page" id="app">
      <div className="loading"></div>
      <div className="m-content joker joker-var" id="main-wrap">
        <div className="main-wrap">
          <Header />
          <div className="form-page">
            <div className="page-wrap">
            <div className="hidden-sm-down">
              <div className="background">
                <div className="gradient">
                  <div className="deco-wave"></div>
                  <div className="deco-line"></div>
                  <div className="deco-inner">
                    <img src="/assets/images/mobile/animation-banner.png" alt="decoration" />
                  </div>
                </div>
              </div>
            </div>
            <div className="parallax">
              <div className="parallax-wrap">
                <div className="inner-parallax large">
                  <div data-enllax-ratio="0" data-enllax-type="foreground">
                    <div className="parallax-figure">
                      <div>
                        <svg className="triangle">
                          <g strokeWidth="1" fill="none" fillRule="evenodd">
                            <polygon
                              strokeWidth="7"
                              transform="translate(24.527903, 30.415493) rotate(-90.000000) translate(-24.527903, -30.415493) "
                              points="24.5279033 12.915493 47.9712995 47.915493 1.08450704 47.915493"
                            ></polygon>
                          </g>
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div data-enllax-ratio="0" data-enllax-type="foreground">
                    <div className="parallax-figure">
                      <div>
                        <svg className="circle">
                          <g strokeWidth="1" fill="none" fillRule="evenodd">
                            <ellipse strokeWidth="10" cx="60" cy="60" rx="49.25" ry="49"></ellipse>
                          </g>
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div data-enllax-ratio="0" data-enllax-type="foreground">
                    <div className="parallax-figure">
                      <div className="square-dot">
                        <svg className="square">
                          <g stroke="none" strokeWidth="1" fillRule="evenodd">
                            <rect x="0" y="0" width="98" height="98" rx="15"></rect>
                          </g>
                        </svg>
                        <img className="dot" src="/assets/images/mobile/dot.svg" alt="dot" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="container inner-wrap max-md">
              <AuthRequiredLink className="waves-effect btn-icon backtohome fragment-fadeUp" to="/">
                <span>
                  <i className="ion-ios-home-outline"></i>
                  <i className="ion-ios-arrow-thin-left"></i>
                </span>
              </AuthRequiredLink>
              <div className="card form-box fragment-fadeUp">
                <div className="full-form-wrap">
                  <h3 className="use-text-title title-contact pb-3 text-center">お問い合わせ</h3>
                  <p className="desc use-text-subtitle2 text-center">
                    ご不明点やご相談がございましたら、<br />以下のフォームよりお問い合わせください。
                  </p>
                  <div className="form">
                    <form id="contact_form" onSubmit={handleSubmit}>
                      <div className="row spacing6">
                        <div className="col-sm-6 px-6">
                          <div className="input-field filled">
                            <input className="validate" id="name" name="name" type="text" required />
                            <label htmlFor="name">名前 *</label>
                          </div>
                        </div>
                        <div className="col-sm-6 px-6">
                          <div className="input-field filled">
                            <input className="validate" id="email" name="email" type="email" required />
                            <label htmlFor="email">メールアドレス *</label>
                          </div>
                        </div>
                        <div className="col-sm-6 px-6">
                          <div className="input-field filled">
                            <input id="companyName" name="companyName" type="text" />
                            <label htmlFor="companyName">会社名（任意）</label>
                          </div>
                        </div>
                        <div className="col-sm-12 px-6">
                          <div className="input-field filled">
                            <textarea className="materialize-textarea" id="message" name="message" rows={6} required></textarea>
                            <label htmlFor="message">お問い合わせ内容 *</label>
                          </div>
                        </div>
                      </div>
                      <div className="btn-area">
                        {submitError ? (
                          <div className="form-control-label" style={{ color: '#d32f2f' }}>
                            {submitError}
                          </div>
                        ) : null}
                        {submitted ? (
                          <div className="form-control-label" style={{ color: '#2e7d32' }}>
                            送信しました。担当者よりご連絡いたします。
                          </div>
                        ) : null}
                        <div className="form-control-label">
                          <label>
                            <input className="filled-in secondary" type="checkbox" name="privacyAccepted" required />
                            <span>
                              {privacyPolicyLink}に同意します *
                            </span>
                          </label>
                        </div>
                        <button className="btn secondary waves-effect" type="submit" disabled={submitting}>
                          {submitting ? '送信中...' : '送信する'}
                          <i className="material-icons right">send</i>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
          <Footer withDeco={true} />
        </div>
      </div>
    </div>
  );
}

